import React from 'react'
import { useTranslation } from 'contexts/Localization'
import { Link } from 'react-router-dom'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { useModal } from '@nominex/dex-uikit'
import { FetchStatus } from 'state/types'
import { useNmxUsdtPrice } from 'hooks/useUsdtPrice'
import { endOfToday } from 'date-fns'
import { MINUTE_IN_SECONDS } from 'config/constants/time'
import useCountdownTimer from 'hooks/useCountdownTimer'
import { Timer } from 'components/Timer'
import ClaimModal from 'components/Claim/ClaimModal'
import { Divider, RowBetween } from 'components/Layout'
import LoadableValue from 'components/LoadableValue'
import { formatNumber } from 'utils/formatters'

export interface RewardsCardFooterProps {
  nextReward: number
  total: number
  bonusesFetchStatus: FetchStatus
  nextRewardFetchStatus: FetchStatus
}

const TIME_OFFSET_IN_MS = new Date().getTimezoneOffset() * MINUTE_IN_SECONDS * 1000

const RewardsCardFooter: React.FC<RewardsCardFooterProps> = ({
  nextReward,
  total,
  bonusesFetchStatus,
  nextRewardFetchStatus,
}) => {
  const { t } = useTranslation()
  const [openClaimModal] = useModal(<ClaimModal />)
  const nmxPrice = Number((useNmxUsdtPrice() || 0).toFixed(8))

  const nextRewardTimestamp = endOfToday().valueOf() - TIME_OFFSET_IN_MS
  const { secondsRemaining } = useCountdownTimer(nextRewardTimestamp)

  const totalUsd = total * nmxPrice
  const nextRewardUsd = nextReward * nmxPrice

  return (
    <div className="flex flex-col w-[100%] gap-8">
      <RowBetween>
        <div className="flex flex-col">
          <div className="text-14">{t('Next daily reward')}</div>
          <Timer secondsRemaining={secondsRemaining} />
        </div>
        <div className="flex flex-col items-end">
          <LoadableValue loading={nextRewardFetchStatus !== FetchStatus.FETCHED} fontSize="14px">
            {formatNumber(nextReward)} NMX
          </LoadableValue>
          <LoadableValue loading={!nmxPrice} color="placeholder" textAlign="end" fontSize="10px">
            ≈ ${formatNumber(nextRewardUsd)}
          </LoadableValue>
        </div>
      </RowBetween>

      <Divider />

      <RowBetween>
        <div className="text-14">{t('Total unclaimed')}</div>
        <div className="flex gap-8 items-center">
          <div className="flex flex-col items-end">
            <LoadableValue loading={bonusesFetchStatus !== FetchStatus.FETCHED} color="success" bold fontSize="14px">
              {formatNumber(total)} NMX
            </LoadableValue>
            <LoadableValue loading={!nmxPrice} color="placeholder" fontSize="10px" textAlign="end">
              ≈ ${formatNumber(totalUsd)}
            </LoadableValue>
          </div>
          <SlButton size="small" variant="success" onClick={openClaimModal}>
            {t('Claim')}
          </SlButton>
        </div>
      </RowBetween>
      <Link className="w-[100%]" to="/bonuses">
        <SlButton className="w-[100%]" variant="primary">
          {t('My rewards')}
        </SlButton>
      </Link>
    </div>
  )
}

export default RewardsCardFooter
